import * as React from 'react';
import styled from 'styled-components';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers';
import * as yup from 'yup';

import { FormServerError } from '../types';
import { PERIODIC } from '../constants/forms';
import {
  DEFAULT_DATE_FORMAT,
  GET_ALL_QUERY_PARAMS,
} from '../constants';
import {
  SchedulingType,
  SchedulingTableType,
} from '../api/scheduling';
import transformServerErrorsToForm from '../utils/transformServerErrorsToForm';
import {
  InputTextWithLabel,
  InputBaseContainer,
  InputSelectWithLabel,
  InputDatePickerWithLabel,
  ButtonWithLoader,
  CheckboxWithLabel,
} from '../molecules';
import formatDate from '../utils/formatDate';
import parseUTCDate from '../utils/parseUTCDate';
import isYupFieldRequired from '../utils/isYupFieldRequired';
import { getFranchiseList } from '../api/franchise';
import useUserRole from '../hooks/useUserRole';

const labels = {
  name: 'Name',
  description: 'Description',
  date: 'Date',
  periodic: 'Recurrence',
  isDefault: 'Set as default in new Branch',
};

const schema = yup.object().shape({
  name: yup.string().required().label(labels.name),
  date: yup.date().required().label(labels.date),
  periodic: yup
    .object()
    .shape({
      value: yup.string(),
      label: yup.string(),
    })
    .required()
    .typeError('Select a periodic type from the list')
    .label(labels.periodic),
  isDefault: yup.boolean().label(labels.isDefault),
});

const SchedulingForm = ({
  onSubmit,
  initialValues,
  modalError,
  addButtonText = 'Add',
  updateButtonText = 'Update',
  initialErrors,
}: Props) => {
  const dateUTC = parseUTCDate(
    initialValues?.date as string,
  );

  const {
    register,
    handleSubmit,
    control,
    errors,
    setError,
    setValue,
    formState,
  } = useForm<SchedulingTableType>({
    resolver: yupResolver(schema),
    defaultValues: initialValues
      ? ({
          ...initialValues,
          date: dateUTC,
          periodic: initialValues.periodic
            ? PERIODIC.find(
                item =>
                  item.value === initialValues.periodic,
              )
            : null,
        } as any)
      : ({} as SchedulingTableType),
  });

  const { isSuperAdmin } = useUserRole();

  const isDeleted = initialValues?.isDeleted;

  const [isSubmitting, setIsSubmitting] = React.useState<
    boolean
  >(false);

  const [isLoading, setIsLoading] = React.useState(false);

  React.useEffect(() => {
    if (isSuperAdmin) {
      setIsLoading(true);

      Promise.all([
        getFranchiseList({
          ...GET_ALL_QUERY_PARAMS,
          ordering: 'name',
        }),
      ])
        .then(([franchisesResponse]) => {
          const franchises = franchisesResponse.data.results.map(
            item => item.id,
          );

          setValue(
            'franchise',
            franchises.filter(Boolean).length
              ? franchises
              : undefined,
          );
        })
        .catch(() => {})
        .finally(() => setIsLoading(false));
    }
  }, []);

  React.useEffect(() => {
    if (
      formState.isSubmitting &&
      !Object.keys(errors).length
    ) {
      setIsSubmitting(formState.isSubmitting);
    }
  }, [formState]);

  React.useEffect(() => {
    if (initialErrors) {
      transformServerErrorsToForm<SchedulingTableType>(
        initialErrors,
      ).forEach(({ name, types }) =>
        setError(name, { types }));
    }
  }, [initialErrors]);

  React.useEffect(() => {
    if (Object.keys(errors).length) {
      setIsSubmitting(false);
    }
  }, [errors, initialErrors]);

  const middleware = (
    submitFn: (data: SchedulingType) => void,
  ) => (data: SchedulingTableType) =>
    submitFn({
      ...data,
      id: initialValues?.id || data.id,
      isDeleted: initialValues?.isDeleted || data.isDeleted,
      date: formatDate(data.date),
      periodic: data.periodic.value,
      franchise: data.franchise
        ? (data.franchise as string)
            .split(',')
            .map(parseFloat)
        : undefined,
    });

  return (
    <Container
      disabled={
        isLoading ||
        isDeleted ||
        (isSubmitting && !modalError)
      }
    >
      <FormContainer
        autoComplete="off"
        aria-autocomplete="none"
        onSubmit={handleSubmit(middleware(onSubmit))}
      >
        <InputBaseContainer>
          <InputTextWithLabel
            name="name"
            error={errors.name}
            inputRef={register}
            label={labels.name}
            isRequired={isYupFieldRequired('name', schema)}
          />
        </InputBaseContainer>
        <InputBaseContainer>
          <InputTextWithLabel
            name="description"
            error={errors.description}
            inputRef={register}
            label={labels.description}
            isRequired={isYupFieldRequired(
              'description',
              schema,
            )}
          />
        </InputBaseContainer>
        <InputBaseContainer>
          <Controller
            name="date"
            defaultValue=""
            valueName="selected"
            onChange={([selected]) => selected}
            control={control}
            render={props => (
              <InputDatePickerWithLabel
                label={labels.date}
                error={errors.date}
                dateFormat={DEFAULT_DATE_FORMAT}
                isRequired={isYupFieldRequired(
                  'date',
                  schema,
                )}
                // eslint-disable-next-line react/prop-types
                selected={props.value}
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
              />
            )}
          />
        </InputBaseContainer>
        <InputBaseContainer>
          <Controller
            name="periodic"
            defaultValue=""
            control={control}
            render={props => (
              <InputSelectWithLabel
                label={labels.periodic}
                error={errors.periodic}
                options={PERIODIC}
                placeholder=""
                isRequired={isYupFieldRequired(
                  'periodic',
                  schema,
                )}
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
              />
            )}
          />
        </InputBaseContainer>
        {isSuperAdmin && (
          <InputBaseContainer>
            <Controller
              name="isDefault"
              control={control}
              valueName="checked"
              defaultValue={
                initialValues?.isDefault ?? true
              }
              render={({ onChange, onBlur, value }) => (
                <CheckboxWithLabel
                  error={errors.isDefault}
                  label={labels.isDefault}
                  checked={value}
                  onBlur={onBlur}
                  onChange={(e, flag) => onChange(flag)}
                />
              )}
            />
          </InputBaseContainer>
        )}

        <input
          type="hidden"
          name="franchise"
          ref={register}
        />

        {!isDeleted && (
          <ButtonWithLoader
            disabled={isSubmitting && !modalError}
            loading={isSubmitting && !modalError}
            type="submit"
          >
            {initialValues
              ? updateButtonText
              : addButtonText}
          </ButtonWithLoader>
        )}
      </FormContainer>
    </Container>
  );
};

const Container = styled.fieldset`
  display: block;
  margin: 0;
  padding: 0;
  width: 100%;
  border: none;
  text-align: center;
`;

const FormContainer = styled.form`
  width: 100%;

  .MuiButton-root {
    margin-top: 24px;
  }
`;

interface Props {
  addButtonText?: string;
  updateButtonText?: string;
  initialValues?: SchedulingType;
  onSubmit: (data: SchedulingTableType) => void;
  initialErrors?: FormServerError<SchedulingTableType>;
  modalError: boolean;
}

export default SchedulingForm;
